<template>
  <div class="report-layout-moderno" style="font-family: 'Inter', sans-serif;">
    <div class="bg-white shadow-sm rounded-xl overflow-hidden mb-8 border border-slate-200 print:shadow-none print:rounded-none">
      <!-- BARRA SUPERIOR (OCULTA NA IMPRESSÃO) -->
      <div class="p-4 border-b flex justify-between items-center print:hidden" style="background-color: #333333; border-color: #444;">
        <div class="flex items-center gap-3">
          <v-icon color="orange" small><slot name="icon">mdi-file-document-outline</slot></v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
            <slot name="title">RELATÓRIO GERAL</slot>
          </h2>
        </div>
        <div class="flex-none ml-auto">
          <button
            class="bg-[#F57C00] hover:bg-orange-600 text-white font-black py-2 px-6 rounded-lg shadow-md shadow-orange-900/20 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest"
            @click="printReport"
            style="width: 150px; flex-basis: 150px; flex-shrink: 0;"
          >
            <v-icon color="white" small class="mr-1" style="font-size: 14px;">mdi-printer</v-icon>
            IMPRIMIR
          </button>
        </div>
      </div>

      <!-- CONTAINER DO RELATÓRIO PÁGINA BRANCA - report-content-print é referência para impressão -->
      <div class="p-8 md:p-12 report-content-print">
        <!-- CABEÇALHO PADRÃO DO SISTEMA (DENTRO DA PÁGINA) -->
        <header v-if="showHeader" class="flex flex-col items-center justify-center mb-10 pb-8 border-b border-slate-100">
          <div class="w-full text-center mb-6">
             <span class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">{{ $store.getters.getAppName }}</span>
          </div>
          
          <img
            v-if="prefecture"
            :src="prefecture.prefecture_logo ? 'https://api.sigfrotagov.com.br/uploads/' + prefecture.prefecture_logo : require('@/assets/no-image.jpg')"
            :alt="prefecture.social_name || ''"
            class="h-24 object-contain mb-6"
          />
          
          <h1 v-if="prefecture" class="text-2xl font-black text-slate-800 uppercase tracking-tight mb-2 text-center">
            {{ prefecture.prefecture_social_name || '' }}
          </h1>
          
          <div class="flex flex-col items-center gap-1">
            <h4 v-if="period" class="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Período: {{ period }}
            </h4>
            <div class="mt-2 text-center">
               <slot name="sub-header"></slot>
            </div>
          </div>
        </header>

        <!-- CONTEÚDO DINÂMICO -->
        <main>
          <slot></slot>
        </main>

        <!-- RODAPÉ DINÂMICO -->
        <footer class="mt-12 break-inside-avoid">
          <slot name="footer"></slot>
        </footer>
      </div> <!-- FIM DO PADDING BRANCO -->
    </div> <!-- FIM DO CONTAINER ARREDONDADO -->
  </div> <!-- FIM DO LAYOUT MODERNO -->
</template>

<script>
/**
 * ReportLayoutModerno
 * Modelo de layout padrão para relatórios SCI Moderno.
 * Permite reutilizar o cabeçalho e estrutura de containers em qualquer relatório.
 */
export default {
  name: "ReportLayoutModerno",
  props: {
    prefecture: {
      type: Object,
      required: true,
      default: () => ({})
    },
    period: {
      type: String,
      default: ""
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    printReport() {
      // Estratégia de impressão via popup isolado:
      // Copia apenas o HTML do relatório para uma nova janela limpa,
      // eliminando 100% dos conflitos de CSS do Vuetify/Sistema.
      const reportEl = this.$el.querySelector('.report-content-print');
      const content = reportEl ? reportEl.innerHTML : this.$el.innerHTML;

      const popup = window.open('', '_blank', 'width=900,height=700');
      popup.document.write(`
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <title>Relatório TCE</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap">
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Inter', sans-serif; background: white; color: #111; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 8px 10px; font-size: 10px; border-bottom: 1px solid #e2e8f0; text-align: left; }
            th { background-color: #1e293b; color: white; font-weight: 900; text-transform: uppercase; }
            tr:hover { background-color: #f8fafc; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .font-bold { font-weight: 700; }
            .font-black { font-weight: 900; }
            .text-orange { color: #ea580c; }
            .text-slate { color: #64748b; }
            header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0; }
            header img { height: 80px; object-fit: contain; margin-bottom: 16px; }
            header h1 { font-size: 20px; font-weight: 900; text-transform: uppercase; }
            header h4 { font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; }
            .totals-box { display: flex; justify-content: flex-end; margin-top: 30px; }
            .totals-inner { background: #1e293b; color: white; border-radius: 12px; padding: 20px 40px; display: flex; gap: 40px; }
            .totals-inner span { display: block; }
            .totals-label { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.15em; }
            .totals-value { font-size: 20px; font-weight: 900; }
            .totals-value.orange { color: #fb923c; }
            .page-break { page-break-before: always; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${content}
        </body>
        </html>
      `);
      popup.document.close();
      popup.focus();
      setTimeout(() => {
        popup.print();
        popup.close();
      }, 800);
    }
  }
}
</script>

<style>
@media print {
  /* ISOLAMENTO AGRESSIVO - FORÇA O RELATÓRIO A SER O ÚNICO ELEMENTO NO PAPEL */
  
  /* 1. Esconde tudo o que não é o layout do relatório ou seus pais */
  body > *:not(.v-application),
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .v-snack,
  .v-dialog__content,
  .print\:hidden {
    display: none !important;
  }

  /* 2. Reseta os containers com posicionamento absoluto para garantir que o relatório "salte" para o topo */
  html, body {
    height: auto !important;
    overflow: visible !important;
    background: white !important;
  }

  #app, .v-application, .v-application--wrap, .v-main, .v-main__wrap, .sci-page-wrapper, .max-w-7xl {
    position: static !important;
    display: block !important;
    height: auto !important;
    min-height: auto !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    box-shadow: none !important;
  }

  /* 3. Posiciona o layout do relatório de forma independente */
  .report-layout-moderno {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    z-index: 9999 !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block !important;
  }

  /* 4. Garante que o texto seja legível e as bordas apareçam */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
    overflow: visible !important;
  }

  /* Remove sombras e paddings excessivos que podem empurrar o conteúdo */
  .p-8, .md:p-12 {
    padding: 20px !important; /* Mantém uma pequena margem para não colar na borda do papel */
  }

  .shadow-sm, .rounded-xl, .border {
    box-shadow: none !important;
    border-radius: 0 !important;
  }
  .page-break {
    page-break-before: always !important;
    break-before: page !important;
  }
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

.report-layout-moderno {
  font-family: 'Inter', sans-serif;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
